import { useState, useEffect } from "react";
import { Employee, EmployeeEvaluation, CriteriaRating, RatingLevel, ratingOptions } from "@/types/employee";
import { Criteria, getDefaultCriteria, getBaselinePoints, getMaxPoints } from "@/types/criteria";
import { loadGlobalCriteria } from "@/lib/criteriaLoader";
import { PerformanceThreshold, getDefaultThresholds, getPerformanceLevelFromThresholds } from "@/types/performanceThresholds";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Save, FileDown, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { exportEvaluationToPDF } from "@/lib/pdfExport";
import { useAuth } from "@/hooks/useAuth";
import RatingSlider from "@/components/RatingSlider";
import { RatingMode } from "@/components/RatingModeSettings";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface EvaluationFormProps {
  employee: Employee;
  onSave: (evaluation: Omit<EmployeeEvaluation, 'id'>) => void;
  initialData?: EmployeeEvaluation;
}

const EvaluationForm = ({ employee, onSave, initialData }: EvaluationFormProps) => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [month, setMonth] = useState(initialData?.month || new Date().toISOString().slice(0, 7));
  const [criteria, setCriteria] = useState<Criteria[]>([]);
  const [thresholds, setThresholds] = useState<PerformanceThreshold[]>([]);
  const [criteriaRatings, setCriteriaRatings] = useState<Record<string, CriteriaRating & { sliderValue?: number }>>(
    initialData?.criteria || {}
  );
  const [ratingMode, setRatingMode] = useState<RatingMode>(() => {
    const stored = localStorage.getItem("ratingMode");
    return (stored as RatingMode) || "slider";
  });

  useEffect(() => {
    const loadData = async () => {
      // Load criteria from database (global settings)
      const loadedCriteria = await loadGlobalCriteria();
      setCriteria(loadedCriteria);

      const storedThresholds = localStorage.getItem("performanceThresholds");
      const loadedThresholds = storedThresholds ? JSON.parse(storedThresholds) : getDefaultThresholds();
      setThresholds(loadedThresholds);

      // Initialize ratings for all active criteria with same logic as EvaluationMatrix
      if (!initialData) {
        const initialRatings: Record<string, CriteriaRating & { sliderValue?: number }> = {};
        loadedCriteria
          .filter((c: Criteria) => c.active)
          .forEach((c: Criteria) => {
            const baseline = getBaselinePoints(c);
            // Bonus criteria start at 0, Standard criteria start at baseline (100%)
            const initialValue = c.criteriaType === 'bonus' ? 0 : baseline;
            initialRatings[c.key] = { 
              rating: c.criteriaType === 'bonus' ? 'nicht-erfuellt' : 'voll-erfuellt',
              comments: [], 
              points: c.defaultPoints,
              sliderValue: initialValue
            };
          });
        setCriteriaRatings(initialRatings);
      }
    };
    
    loadData();
  }, [initialData]);

  const handleRatingChange = (criteriaKey: string, rating: RatingLevel) => {
    setCriteriaRatings(prev => ({
      ...prev,
      [criteriaKey]: { ...prev[criteriaKey], rating }
    }));
  };

  const handleCommentChange = async (criteriaKey: string, comment: string) => {
    if (!comment.trim()) return;
    
    const userId = user?.id || "local-user";
    const userName = profile?.full_name || user?.email || "Lokaler Benutzer";
    
    setCriteriaRatings(prev => {
      const existingComments = prev[criteriaKey]?.comments || [];
      return {
        ...prev,
        [criteriaKey]: { 
          ...prev[criteriaKey], 
          comments: [...existingComments, { 
            text: comment, 
            date: new Date().toISOString(),
            userId,
            userName
          }]
        }
      };
    });
  };

  const handlePointsChange = (criteriaKey: string, points: number) => {
    setCriteriaRatings(prev => ({
      ...prev,
      [criteriaKey]: { ...prev[criteriaKey], points }
    }));
  };

  const handleSliderChange = (criteriaKey: string, value: number, maxPoints: number) => {
    // Convert slider value to rating level for consistency
    const percentage = maxPoints > 0 ? (value / maxPoints) * 100 : 0;
    let rating: RatingLevel;
    if (percentage === 100) rating = 'voll-erfuellt';
    else if (percentage >= 75) rating = 'erfuellt';
    else if (percentage >= 50) rating = 'teilweise-erfuellt';
    else rating = 'nicht-erfuellt';
    
    setCriteriaRatings(prev => ({
      ...prev,
      [criteriaKey]: { 
        ...prev[criteriaKey], 
        rating,
        sliderValue: value
      }
    }));
  };

  const calculateScore = () => {
    let totalScore = 0;
    let maxScore = 0;
    let baselineScore = 0; // 100% reference - only standard criteria count

    criteria.forEach((criterion) => {
      const rating = criteriaRatings[criterion.key];
      if (!rating) return;

      const baseline = getBaselinePoints(criterion);
      const criterionMax = getMaxPoints(criterion);
      const weight = criterion.weight || 1;
      
      maxScore += criterionMax * weight;
      
      // IMPORTANT: Only STANDARD criteria count towards the 100% baseline
      // Bonus criteria are EXTRA points that can push score ABOVE 100%
      if (criterion.criteriaType !== 'bonus') {
        baselineScore += baseline * weight;
      }
      
      // Use slider value if in slider mode and sliderValue exists
      if (ratingMode === 'slider' && rating.sliderValue !== undefined) {
        totalScore += rating.sliderValue * weight;
      } else {
        // Use checkbox rating mode
        if (rating.rating === 'voll-erfuellt') {
          totalScore += baseline * weight;
        } else if (rating.rating === 'erfuellt') {
          totalScore += baseline * 0.75 * weight;
        } else if (rating.rating === 'teilweise-erfuellt') {
          totalScore += baseline * 0.5 * weight;
        }
      }
    });

    return { totalScore, maxScore, baselineScore };
  };

  const getPerformanceLevel = (score: number, maxScore: number) => {
    return getPerformanceLevelFromThresholds(score, maxScore, thresholds);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const { totalScore, baselineScore } = calculateScore();
    // Use baselineScore as 100% reference (same as Matrix)
    const performanceLevel = getPerformanceLevel(totalScore, baselineScore);

    const evaluation: Omit<EmployeeEvaluation, 'id'> = {
      employeeId: employee.id,
      month,
      date: new Date().toISOString(),
      criteria: criteriaRatings as any,
      totalScore,
      maxScore: baselineScore, // Store baselineScore as reference for 100%
      performanceLevel,
    };

    onSave(evaluation);
    toast.success("Bewertung erfolgreich gespeichert!");
    navigate(`/employee/${employee.id}`);
  };

  const handleExportPDF = async () => {
    const { totalScore, baselineScore } = calculateScore();
    const performanceLevel = getPerformanceLevel(totalScore, baselineScore);

    const evaluation: EmployeeEvaluation = {
      id: crypto.randomUUID(),
      employeeId: employee.id,
      month,
      date: new Date().toISOString(),
      criteria: criteriaRatings as any,
      totalScore,
      maxScore: baselineScore,
      performanceLevel,
    };

    // Use global criteria from database (single source of truth)
    const loadedCriteria = await loadGlobalCriteria();

    exportEvaluationToPDF(employee, evaluation, loadedCriteria);
    toast.success("PDF wurde erstellt");
  };

  const { totalScore, baselineScore } = calculateScore();
  const performanceLevel = getPerformanceLevel(totalScore, baselineScore);
  const percentageOfBaseline = baselineScore > 0 ? (totalScore / baselineScore) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-3xl font-bold text-foreground">Bewertung für {employee.name}</h2>
          <p className="text-muted-foreground mt-1">{employee.position}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Bewertungszeitraum</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="month">Monat</Label>
              <Input
                id="month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                required
              />
            </div>
          </CardContent>
        </Card>

        {criteria.map((criterion) => (
          <Card key={criterion.id}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                {criterion.label}
                {criterion.description && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p className="text-sm">{criterion.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ratingMode === 'slider' ? (
                <div className="space-y-4">
                  <RatingSlider
                    value={criteriaRatings[criterion.key]?.sliderValue ?? criterion.defaultPoints}
                    maxValue={criteriaRatings[criterion.key]?.points || criterion.defaultPoints}
                    onChange={(value) => handleSliderChange(
                      criterion.key, 
                      value, 
                      criteriaRatings[criterion.key]?.points || criterion.defaultPoints
                    )}
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Maximale Punktzahl für dieses Kriterium</Label>
                    <Input
                      type="number"
                      min="0"
                      value={criteriaRatings[criterion.key]?.points || criterion.defaultPoints}
                      onChange={(e) => handlePointsChange(criterion.key, parseInt(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Bewertung</Label>
                    <RadioGroup
                      value={criteriaRatings[criterion.key]?.rating || ""}
                      onValueChange={(value) => handleRatingChange(criterion.key, value as RatingLevel)}
                    >
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {ratingOptions.map(({ value, label }) => (
                          <div key={value} className="flex items-center space-x-2 border rounded-lg p-3 hover:bg-accent/50 transition-colors">
                            <RadioGroupItem value={value} id={`${criterion.key}-${value}`} />
                            <Label htmlFor={`${criterion.key}-${value}`} className="cursor-pointer flex-1">
                              {label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor={`comment-${criterion.key}`}>Kommentare</Label>
                <div className="space-y-2 mb-3">
                  {criteriaRatings[criterion.key]?.comments?.map((comment, idx) => (
                    <div key={idx} className="p-3 bg-muted rounded-lg border">
                      <div className="flex justify-between items-start mb-1">
                        <div className="text-xs font-medium text-muted-foreground">
                          {new Date(comment.date).toLocaleDateString('de-DE', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </div>
                        <div className="text-xs text-primary font-medium">
                          {comment.userName}
                        </div>
                      </div>
                      <div className="text-sm">{comment.text}</div>
                    </div>
                  ))}
                </div>
                <Textarea
                  id={`comment-${criterion.key}`}
                  placeholder="Neuen Kommentar hinzufügen und Enter drücken..."
                  onBlur={(e) => {
                    if (e.target.value.trim()) {
                      handleCommentChange(criterion.key, e.target.value);
                      e.target.value = '';
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const target = e.target as HTMLTextAreaElement;
                      if (target.value.trim()) {
                        handleCommentChange(criterion.key, target.value);
                        target.value = '';
                      }
                    }
                  }}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        ))}

        <Card className="bg-primary/5 border-primary/20">
          <CardHeader>
            <CardTitle>Gesamtbewertung</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-card rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Erreichte Punkte</p>
                <p className="text-3xl font-bold text-primary">{totalScore.toFixed(1)}</p>
              </div>
              <div className="text-center p-4 bg-card rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Soll-Punkte (100%)</p>
                <p className="text-3xl font-bold">{baselineScore}</p>
              </div>
              <div className="text-center p-4 bg-card rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Prozent</p>
                <p className={`text-3xl font-bold ${percentageOfBaseline > 100 ? 'text-green-500' : percentageOfBaseline >= 75 ? 'text-primary' : 'text-orange-500'}`}>
                  {percentageOfBaseline.toFixed(0)}%
                </p>
              </div>
              <div className="text-center p-4 bg-card rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Leistungsniveau</p>
                <p className="text-xl font-bold text-accent">{performanceLevel}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Abbrechen
          </Button>
          <Button type="button" variant="secondary" onClick={handleExportPDF} className="gap-2">
            <FileDown className="h-4 w-4" />
            Als PDF exportieren
          </Button>
          <Button type="submit" className="gap-2">
            <Save className="h-4 w-4" />
            Bewertung speichern
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EvaluationForm;
